import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CardGiftcardRounded, WorkspacePremiumRounded } from '@mui/icons-material';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import { scholarshipApi, type ProgramScholarshipAwardDto } from '../../../lib/api/scholarship.api';
import { errorText } from '../../../lib/error-text';

interface Props {
  studentId: string;
}

export function StudentScholarshipsTab({ studentId }: Props): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const queryClient = useQueryClient();

  const [programNames, setProgramNames] = useState<Map<string, string>>(new Map());
  const [programId, setProgramId] = useState('');
  const [name, setName] = useState('');
  const [percent, setPercent] = useState('');
  const [notes, setNotes] = useState('');
  const [waiveEnrollment, setWaiveEnrollment] = useState(false);
  const [feedback, setFeedback] = useState<{ severity: 'success' | 'error'; text: string } | null>(
    null,
  );

  // Program names for the selector + award labels.
  useEffect(() => {
    if (!currentTenantId) return;
    const unsub = onSnapshot(collection(db, `tenants/${currentTenantId}/programs`), (snap) => {
      const m = new Map<string, string>();
      snap.docs.forEach((d) => m.set(d.id, (d.data()['name'] as string | undefined) ?? '—'));
      setProgramNames(m);
    });
    return () => unsub();
  }, [currentTenantId]);

  const awardsQuery = useQuery({
    queryKey: ['programAwards', currentTenantId, studentId],
    queryFn: () =>
      scholarshipApi.listProgramScholarshipAwards({ tenantId: currentTenantId!, studentId }),
    enabled: !!currentTenantId,
  });

  const awards = awardsQuery.data ?? [];
  const activeAwards = useMemo(() => awards.filter((a) => a.status === 'active'), [awards]);
  const revokedAwards = useMemo(() => awards.filter((a) => a.status === 'revoked'), [awards]);

  const assignMutation = useMutation({
    mutationFn: () =>
      scholarshipApi.assignProgramScholarship({
        tenantId: currentTenantId!,
        studentId,
        programId,
        name: name.trim(),
        coveragePercent: Number(percent),
        notes: notes.trim() || null,
        waiveEnrollment,
      }),
    onSuccess: (res) => {
      setFeedback({
        severity: 'success',
        text: t('people.profile.scholarships.assignedResult', {
          applied: res.plansApplied,
          conflict: res.plansInConflict,
        }),
      });
      setProgramId('');
      setName('');
      setPercent('');
      setNotes('');
      setWaiveEnrollment(false);
      void queryClient.invalidateQueries({
        queryKey: ['programAwards', currentTenantId, studentId],
      });
    },
    onError: (e: unknown) => {
      setFeedback({
        severity: 'error',
        text: errorText(e, t('people.profile.scholarships.assignError')),
      });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (awardId: string) =>
      scholarshipApi.revokeProgramScholarship({ tenantId: currentTenantId!, awardId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['programAwards', currentTenantId, studentId],
      });
    },
  });

  const pctNum = Number(percent);
  const canAssign =
    !!programId && !!name.trim() && pctNum > 0 && pctNum <= 100 && !assignMutation.isPending;

  return (
    <Box sx={{ py: 2 }}>
      <Stack spacing={3}>
        {feedback && (
          <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
            {feedback.text}
          </Alert>
        )}

        {/* ── Active fixed awards ─────────────────────────────────────────────── */}
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <WorkspacePremiumRounded color="secondary" fontSize="small" />
              <Typography variant="h6" fontWeight="600">
                {t('people.profile.scholarships.activeAwards')}
              </Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {awardsQuery.isLoading ? (
              <CircularProgress size={22} />
            ) : activeAwards.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {t('people.profile.scholarships.noAwards')}
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {activeAwards.map((a: ProgramScholarshipAwardDto) => (
                  <Stack
                    key={a.id}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}
                  >
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CardGiftcardRounded color="secondary" sx={{ fontSize: 18 }} />
                        <Typography variant="body1" fontWeight="600">
                          {a.name}
                        </Typography>
                        <Chip
                          size="small"
                          color="secondary"
                          label={t('people.profile.scholarships.awardCoverage', {
                            pct: a.coveragePercent,
                          })}
                        />
                        {a.waiveEnrollment && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="warning"
                            label={t('people.profile.scholarships.enrollmentWaived')}
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {programNames.get(a.programId) ?? a.programId}
                        {a.notes ? ` — ${a.notes}` : ''}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      disabled={revokeMutation.isPending}
                      onClick={() => {
                        if (window.confirm(t('people.profile.scholarships.confirmRevoke'))) {
                          revokeMutation.mutate(a.id);
                        }
                      }}
                    >
                      {t('people.profile.scholarships.revoke')}
                    </Button>
                  </Stack>
                ))}
              </Stack>
            )}

            {revokedAwards.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('people.profile.scholarships.revokedHistory')}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {revokedAwards.map((a) => (
                    <Chip
                      key={a.id}
                      size="small"
                      variant="outlined"
                      label={`${a.name} · ${a.coveragePercent}%`}
                      sx={{ opacity: 0.6 }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* ── Assign a new fixed award ────────────────────────────────────────── */}
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 0.5 }}>
              {t('people.profile.scholarships.assignTitle')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('people.profile.scholarships.percentHint')}
            </Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                select
                fullWidth
                size="small"
                label={t('people.profile.scholarships.program')}
                value={programId}
                onChange={(e) => setProgramId(e.target.value)}
              >
                <MenuItem value="" disabled>
                  {t('people.profile.scholarships.selectProgram')}
                </MenuItem>
                {[...programNames.entries()].map(([id, pname]) => (
                  <MenuItem key={id} value={id}>
                    {pname}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                size="small"
                label={t('people.profile.scholarships.name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                type="number"
                label={t('people.profile.scholarships.coveragePercent')}
                value={percent}
                onChange={(e) => setPercent(e.target.value)}
                inputProps={{ min: 1, max: 100 }}
              />
              <TextField
                fullWidth
                size="small"
                label={t('people.profile.scholarships.notes')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={waiveEnrollment}
                    onChange={(e) => setWaiveEnrollment(e.target.checked)}
                  />
                }
                label={t('people.profile.scholarships.waiveEnrollment')}
              />
              <Box>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={!canAssign}
                  onClick={() => assignMutation.mutate()}
                  startIcon={
                    assignMutation.isPending ? (
                      <CircularProgress size={16} />
                    ) : (
                      <CardGiftcardRounded />
                    )
                  }
                >
                  {t('people.profile.scholarships.assign')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
